// "Needs action" (UI design §3.2): every item is a link to the exact record with the action in view.
import { transferEndKey } from '@fleetspoke/core/places';
import type { ActionItem, ActionKind, TagRow } from '../data/dashboard';
import { calendarDate, localeTag, relativeTime } from './time';

export function actionLink(a: ActionItem): string {
  switch (a.kind) {
    case 'transfer_awaiting':
    case 'transfer_overdue':
    case 'transfer_stuck':
      return `/transfers/${a.ref_id}`;
    case 'asset_missing':
    case 'tag_low_battery':
    case 'retire_tag_removal':
    case 'cert_expiring':
      return `/assets/${a.ref_id}`;
    case 'gateway_offline':
      return `/sites/${a.site_id ?? ''}`;
    case 'count_open':
      return `/counts/${a.ref_id}`;
    case 'count_due':
    case 'count_overdue':
      // the site's Stock counts tab (/sites/:id redirects to the warehouse or project page)
      return a.site_id ? `/sites/${a.site_id}?tab=counts` : '/warehouses';
    case 'deployment_overdue':
      return `/projects/${a.ref_id}`;
    case 'request_pending':
    case 'request_to_prepare':
    case 'request_late':
      return `/requests/${a.ref_id}`;
  }
}

type T = (key: string, opts?: Record<string, unknown>) => string;

/** One line per item; `age` is a ready relative time ("26 hr ago") or null. */
export function actionText(a: ActionItem, t: T, age: string | null): string {
  const p = a.params;
  const when = age ?? t('common.never');
  switch (a.kind) {
    case 'transfer_awaiting':
      return t('dashboard.a_transfer_awaiting', {
        ref: p.ref,
        from: p.from ?? t(transferEndKey(null, 'from')),
        to: p.to ?? t(transferEndKey(null, 'to')),
        count: p.assets ?? 0,
        age: when,
      });
    case 'transfer_overdue':
      return t('dashboard.a_transfer_overdue', {
        ref: p.ref,
        from: p.from ?? t(transferEndKey(null, 'from')),
        to: p.to ?? t(transferEndKey(null, 'to')),
        age: when,
      });
    case 'transfer_stuck':
      return t('dashboard.a_transfer_stuck', {
        ref: p.ref,
        from: p.from ?? t(transferEndKey(null, 'from')),
        to: p.to ?? t(transferEndKey(null, 'to')),
        age: when,
      });
    case 'asset_missing':
      return t('dashboard.a_asset_missing', {
        code: p.code,
        category: p.category ?? '—',
        site: p.site ?? '—',
        age: when,
      });
    case 'gateway_offline':
      return t('dashboard.a_gateway_offline', { gateway: p.gateway, site: p.site, age: when });
    case 'count_open':
      return t('dashboard.a_count_open', { site: p.site, count: p.not_found ?? 0 });
    case 'count_due':
      return t('dashboard.a_count_due_today', { site: p.site, time: p.due_time ?? '—' });
    case 'count_overdue':
      return t('dashboard.a_count_overdue', { site: p.site, time: p.due_time ?? '—' });
    case 'tag_low_battery':
      return t('dashboard.a_low_battery', { code: p.code, battery: p.battery ?? '?' });
    case 'deployment_overdue':
      return t('dashboard.a_deployment_overdue', { code: p.code, project: p.project, age: when });
    case 'request_pending':
    case 'request_to_prepare':
    case 'request_late':
      return t(`dashboard.a_${a.kind}`, {
        ref: p.ref,
        site: p.site ?? '—',
        lines: p.lines ?? '—',
        date: p.needed_by ?? '—',
        urgent: p.priority === 'urgent' ? t('requests.urgent_suffix') : '',
      });
    case 'retire_tag_removal':
      return t('retire.action_item', {
        code: p.code,
        tags: Array.isArray(p.tags) ? p.tags.join(', ') : '—',
        site: p.site ?? '—',
        age: when,
      });
    case 'cert_expiring':
      return t('dashboard.a_cert_expiring', {
        code: p.code,
        kind: p.kind,
        date: p.expires_on ?? '—',
      });
  }
}

// The dashboard's Needs action rows (CR_20260924_Dashboard_Revamp §3.3): the same items split into a kind label, a
// title, a detail line and an age column. `actionText` stays the one-line version (the row's accessible name).

export type ActionGroup =
  'missing' | 'transfers' | 'counts' | 'requests' | 'certificates' | 'devices' | 'projects';
/** Filter chip order. */
export const ACTION_GROUPS: readonly ActionGroup[] = [
  'missing',
  'transfers',
  'counts',
  'requests',
  'certificates',
  'devices',
  'projects',
];
export const ACTION_GROUP_LABEL: Record<ActionGroup, string> = {
  missing: 'dashboard.f_missing',
  transfers: 'dashboard.f_transfers',
  counts: 'dashboard.f_counts',
  requests: 'dashboard.f_requests',
  certificates: 'dashboard.f_certificates',
  devices: 'dashboard.f_devices',
  projects: 'dashboard.f_projects',
};

export function actionGroup(kind: ActionKind): ActionGroup {
  switch (kind) {
    case 'asset_missing':
      return 'missing';
    case 'transfer_awaiting':
    case 'transfer_overdue':
    case 'transfer_stuck':
      return 'transfers';
    case 'count_open':
    case 'count_due':
    case 'count_overdue':
      return 'counts';
    case 'request_pending':
    case 'request_to_prepare':
    case 'request_late':
      return 'requests';
    case 'gateway_offline':
    case 'tag_low_battery':
    case 'retire_tag_removal':
      return 'devices';
    case 'deployment_overdue':
      return 'projects';
    case 'cert_expiring':
      return 'certificates';
  }
}

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 } as const;

/** Critical first, then warning, then info; within a severity the oldest first (no time last). */
export function sortActions(items: readonly ActionItem[]): ActionItem[] {
  const at = (a: ActionItem) => (a.since ? new Date(a.since).getTime() : Infinity);
  return [...items].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || at(a) - at(b),
  );
}

/** "12 days" / "5 hours" / "3 minutes" since (or until) an instant, without "ago": the age column's bold part. */
export function elapsed(iso: string, language: string, now: number = Date.now()): string {
  const seconds = Math.abs(now - new Date(iso).getTime()) / 1000;
  const [value, unit] =
    seconds < 3600
      ? [Math.max(1, Math.round(seconds / 60)), 'minute']
      : seconds < 86400
        ? [Math.round(seconds / 3600), 'hour']
        : [Math.round(seconds / 86400), 'day'];
  return new Intl.NumberFormat(localeTag(language), {
    style: 'unit',
    unit,
    unitDisplay: 'long',
  }).format(value);
}

/** Who is looking: the transfer wording depends on which side of it they are. */
export interface ActionViewer {
  manager: boolean;
  /** the viewer works at this place (`canActAt`) */
  atSite: (siteId: string | null) => boolean;
  /** today in the company timezone (YYYY-MM-DD) */
  today: string;
  /**
   * A place's name when the item carries none — the sender of a transfer may not read its destination (RLS on sites);
   * `transfer_places()` still names it, as on the transfer page.
   */
  placeName?: (siteId: string | null) => string | undefined;
}

export interface ActionParts {
  group: ActionGroup;
  kind: string;
  title: string;
  detail: string;
  /** the age column: a bold line and a small word under it (either may be empty) */
  age: string;
  ageNote: string;
}

type Age = { age: string; ageNote: string };

export function actionParts(
  a: ActionItem,
  t: T,
  now: number,
  language: string,
  viewer: ActionViewer,
): ActionParts {
  const p = a.params;
  const dash = '—';
  const ago = relativeTime(a.since, language, now) ?? t('common.never');
  const since = (note: 'dashboard.age_ago' | 'dashboard.age_waiting'): Age =>
    a.since
      ? { age: elapsed(a.since, language, now), ageNote: t(note) }
      : { age: dash, ageNote: '' };
  // a transfer reads by the viewer's side: a manager sees both ends, anyone else is the receiver or the sender
  const key =
    a.kind === 'transfer_awaiting' && !viewer.manager
      ? viewer.atSite(a.site_id)
        ? 'transfer_awaiting_in'
        : 'transfer_awaiting_out'
      : a.kind;
  // an overdue count from an earlier day names its date ("today" would be wrong)
  const detailKey =
    a.kind === 'count_overdue' && typeof p.date === 'string' && p.date !== viewer.today
      ? 'dashboard.d_count_overdue_date'
      : // a stuck transfer whose assets are still seen at the origin never left: say so
        a.kind === 'transfer_stuck' && Number(p.at_origin ?? 0) > 0
        ? 'dashboard.d_transfer_stuck_origin'
        : a.kind === 'cert_expiring' && a.severity === 'critical'
          ? 'dashboard.d_cert_expired'
          : `dashboard.d_${key}`;
  const parts = (vars: Record<string, unknown>, age: Age): ActionParts => ({
    group: actionGroup(a.kind),
    kind: t(`dashboard.k_${key}`),
    title: t(`dashboard.t_${key}`, vars),
    // a receiver may not see the sending warehouse (RLS on sites): no "From —", just no detail line
    detail: key === 'transfer_awaiting_in' && !p.from ? '' : t(detailKey, vars),
    ...age,
  });
  switch (a.kind) {
    case 'transfer_awaiting':
      return parts(
        {
          ref: p.ref,
          from: p.from ?? dash,
          to: p.to ?? viewer.placeName?.(a.site_id) ?? dash,
          count: Number(p.assets ?? 0),
        },
        since('dashboard.age_waiting'),
      );
    case 'transfer_overdue':
      return parts(
        { ref: p.ref, from: p.from ?? dash, to: p.to ?? dash, age: ago },
        since('dashboard.age_ago'),
      );
    case 'transfer_stuck':
      return parts(
        {
          ref: p.ref,
          from: p.from ?? dash,
          to: p.to ?? dash,
          count: Number(p.assets ?? 0),
          at_origin: Number(p.at_origin ?? 0),
        },
        since('dashboard.age_ago'),
      );
    case 'asset_missing':
      return parts(
        { code: p.code, category: p.category ?? dash, site: p.site ?? dash },
        since('dashboard.age_ago'),
      );
    case 'gateway_offline':
      return parts(
        { gateway: p.gateway, site: p.site ?? dash, age: ago },
        since('dashboard.age_ago'),
      );
    case 'count_open':
      return parts(
        { site: p.site ?? dash, count: p.not_found ?? 0 },
        { age: t('dashboard.age_open'), ageNote: '' },
      );
    case 'count_due':
      return parts(
        { site: p.site ?? dash, time: p.due_time ?? dash },
        { age: t('dashboard.age_due'), ageNote: typeof p.due_time === 'string' ? p.due_time : '' },
      );
    case 'count_overdue':
      return parts(
        {
          site: p.site ?? dash,
          time: p.due_time ?? dash,
          date: calendarDate(typeof p.date === 'string' ? p.date : null, language) ?? dash,
        },
        {
          age: t('dashboard.age_overdue'),
          ageNote: p.due_time ? t('dashboard.age_since', { time: p.due_time }) : '',
        },
      );
    case 'tag_low_battery':
      return parts({ code: p.code, battery: p.battery ?? '?' }, since('dashboard.age_ago'));
    case 'deployment_overdue':
      return parts({ code: p.code, project: p.project ?? dash }, since('dashboard.age_ago'));
    case 'request_pending':
    case 'request_to_prepare':
    case 'request_late': {
      const date = calendarDate(typeof p.needed_by === 'string' ? p.needed_by : null, language);
      return parts(
        {
          ref: p.ref,
          site: p.site ?? dash,
          lines: p.lines ?? dash,
          date: date ?? dash,
          urgent: p.priority === 'urgent' ? t('requests.urgent_suffix') : '',
        },
        date ? { age: date, ageNote: t('dashboard.age_needed') } : since('dashboard.age_waiting'),
      );
    }
    case 'retire_tag_removal':
      return parts(
        {
          code: p.code,
          tags: Array.isArray(p.tags) ? p.tags.join(', ') : dash,
          site: p.site ?? dash,
        },
        since('dashboard.age_waiting'),
      );
    case 'cert_expiring': {
      const date =
        calendarDate(typeof p.expires_on === 'string' ? p.expires_on : null, language) ?? dash;
      return parts(
        { code: p.code, kind: p.kind ?? dash, date },
        {
          age: date,
          ageNote: t(a.severity === 'critical' ? 'dashboard.age_expired' : 'dashboard.age_expires'),
        },
      );
    }
  }
}

export type TagHealth = 'never' | 'silent_48h' | 'silent_24h' | 'low_battery' | 'ok';
export const TAG_HEALTH_ORDER: TagHealth[] = [
  'never',
  'silent_48h',
  'silent_24h',
  'low_battery',
  'ok',
];
export const LOW_BATTERY_PCT = 15; // same default as the low_battery alert rule

/** Worst problem first: never heard, silent > 48 h, silent > 24 h, battery low, fine. */
export function tagHealth(tag: TagRow, now: number = Date.now()): TagHealth {
  if (!tag.last_seen_at) return 'never';
  const hours = (now - new Date(tag.last_seen_at).getTime()) / 3_600_000;
  if (hours > 48) return 'silent_48h';
  if (hours > 24) return 'silent_24h';
  if (tag.battery_pct != null && tag.battery_pct < LOW_BATTERY_PCT) return 'low_battery';
  return 'ok';
}
